import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import LocationsTable from "../../components/admin/locations/LocationsTable";
import LocationDetailsDrawer from "../../components/admin/locations/LocationDetailsDrawer";
import { TablePage } from "@outpave/ds";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";

const enum LocationStatus {
  ACTIVE = "ACTIVE",
  CLOSED = "CLOSED",
}

export type LocationFilterCriteriaProps = {
  status: LocationStatus;
  searchTerm: string;
};

const Locations: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Locations"));
  const [openLocationDetailsDrawer, setOpenLocationDetailsDrawer] =
    useState<boolean>(false);

  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [refreshLocations, setrefreshLocations] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [editMode, setEditMode] = useState(false);

  const [filterCriteria, setFilterCriteria] =
    useState<LocationFilterCriteriaProps>({
      status: LocationStatus.ACTIVE,
      searchTerm: "",
    });

  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });

  const tabConfigurations = [
    {
      label: t("Active"),
      status: LocationStatus.ACTIVE,
    },
    {
      label: t("Closed"),
      status: LocationStatus.CLOSED,
    },
  ];

  useEffect(() => {
    setFilterCriteria({
      status: tabConfigurations[activeTab].status,
      searchTerm: searchTerm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm]);

  const handleRefreshLocations = () => {
    setrefreshLocations((prev) => prev + 1);
  };

  const handleOpenLocationDetailsDrawer = () => {
    setOpenLocationDetailsDrawer(true);
  };

  const handleCloseLocationDetailsDrawer = () => {
    setSelectedAddressId("");
    setOpenLocationDetailsDrawer(false);
    setEditMode(false);
  };

  const handleOpenLocationsDrawer = (id: string, editMode: boolean = false) => {
    handleOpenLocationDetailsDrawer();
    setEditMode(editMode);
    setSelectedAddressId(id);
  };

  const handleSuccess = () => {
    handleRefreshLocations();
    setSelectedAddressId("");
  };

  return (
    <TablePage
      title={t("Locations")}
      breadcrumbs={breadcrumbs}
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      primaryAction={
        !isAuditor && (
          <Button onClick={handleOpenLocationDetailsDrawer}>
            {t("Add Location")}
          </Button>
        )
      }
    >
      <LocationsTable
        filterCriteria={filterCriteria}
        handleOpenLocationsDrawer={handleOpenLocationsDrawer}
        refreshLocations={refreshLocations}
      />

      <LocationDetailsDrawer
        handleSuccess={handleSuccess}
        open={openLocationDetailsDrawer}
        editMode={editMode}
        handleCloseDrawer={handleCloseLocationDetailsDrawer}
        selectedAddressId={selectedAddressId}
      />
    </TablePage>
  );
};

export default Locations;
